import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AppButton from "../button/AppButton";
import { AdminAccountResponse } from "../../models/ResponseModels/AdminAccountResponse";
import { formatDate } from "../../utils/helperFunctions/dateFormatter";
import { readableText } from "../../utils/helperFunctions/stringFormatter";
import { AdminUserResponse } from "../../models/ResponseModels/AdminUserResponse";
import { AdminPropertyResponse } from "../../models/ResponseModels/AdminPropertyResponse";
import { AdminUnitResponse } from "../../models/ResponseModels/AdminUnitResponse";

interface AccountDetailsModalProps {
  open: boolean;
  account: AdminAccountResponse | null;
  onClose: () => void;
  onOpenSubscription: () => void;
  onOpenCleaner: () => void;
  onToggleUserStatus: (user: AdminUserResponse) => void;
  onDeleteUser: (user: AdminUserResponse) => void;
  onDeleteAccount: () => void;
  onAddProperty: () => void;
  onEditProperty: (property: AdminPropertyResponse) => void;
  onAddUnit: (propertyId?: number) => void;
  onEditUnit: (unit: AdminUnitResponse) => void;
}

const getStatusColor = (status?: string) => {
  if (status === "active") {
    return "success";
  }

  if (status === "trial") {
    return "warning";
  }

  if (status === "expired" || status === "cancelled") {
    return "error";
  }

  return "default";
};

const getRoleLabel = (role: string) => {
  if (role === "cleaner") {
    return "Housekeeper";
  }

  if (role === "viewer") {
    return "Viewer";
  }

  if (role === "manager") {
    return "Manager";
  }

  if (role === "owner") {
    return "Owner";
  }

  return readableText(role);
};

const formatMoney = (
  amount: number | null | undefined,
  currency: string | null | undefined,
) => {
  if (amount === null || amount === undefined) {
    return "-";
  }

  return `${(amount / 100).toFixed(2)} ${currency || "EUR"}`;
};

const AccountDetailsModal = ({
  open,
  account,
  onClose,
  onOpenSubscription,
  onOpenCleaner,
  onToggleUserStatus,
  onDeleteUser,
  onDeleteAccount,
  onAddProperty,
  onEditProperty,
  onAddUnit,
  onEditUnit,
}: AccountDetailsModalProps) => {
  const users = account?.users || [];
  const properties = account?.properties || [];
  const owners = users.filter((user) => user.role === "owner");
  const managers = users.filter((user) => user.role === "manager");
  const cleaners = users.filter((user) => user.role === "cleaner");
  const viewers = users.filter((user) => user.role === "viewer");

  const customMonthlyTotal =
    account?.custom_unit_limit && account?.custom_unit_price_amount
      ? account.custom_unit_limit * account.custom_unit_price_amount
      : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{account?.name || "Account details"}</DialogTitle>

      <DialogContent>
        {!account ? null : (
          <Stack spacing={3}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(4, 1fr)",
                },
                gap: 2,
              }}
            >
              <InfoBlock
                label="Plan"
                value={readableText(account.subscription_plan)}
              />

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={readableText(account.subscription_status)}
                  color={getStatusColor(account.subscription_status) as any}
                  size="small"
                  sx={{ fontWeight: 800 }}
                />
              </Box>

              <InfoBlock
                label="Valid until"
                value={formatDate(account.subscription_valid_until)}
              />

              <InfoBlock
                label="Remaining days"
                value={String(account.remaining_days ?? "-")}
              />

              {account.subscription_plan === "custom" ? (
                <>
                  <InfoBlock
                    label="Contracted units"
                    value={String(account.custom_unit_limit ?? "-")}
                  />

                  <InfoBlock
                    label="Price per unit/month"
                    value={formatMoney(
                      account.custom_unit_price_amount,
                      account.custom_unit_price_currency,
                    )}
                  />

                  <InfoBlock
                    label="Contracted monthly total"
                    value={formatMoney(
                      customMonthlyTotal,
                      account.custom_unit_price_currency,
                    )}
                  />

                  <InfoBlock
                    label="Configured units"
                    value={String(account.units_count ?? 0)}
                  />
                </>
              ) : null}

              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="body2" color="text.secondary">
                  Readable status
                </Typography>
                <Typography sx={{ fontWeight: 800 }}>
                  {account.readable_subscription_status || "-"}
                </Typography>
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="body2" color="text.secondary">
                  Notes
                </Typography>
                <Typography>{account.notes || "-"}</Typography>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Box
                sx={{
                  mb: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Properties and units
                </Typography>

                <Stack direction="row" spacing={1}>
                  <AppButton variant="outlined" onClick={onAddProperty}>
                    Add property
                  </AppButton>

                  <AppButton
                    variant="outlined"
                    onClick={() => onAddUnit()}
                    disabled={properties.length === 0}
                  >
                    Add unit
                  </AppButton>
                </Stack>
              </Box>

              <Stack spacing={1.5}>
                {properties.length === 0 ? (
                  <Typography color="text.secondary">
                    No properties configured.
                  </Typography>
                ) : (
                  properties.map((property) => (
                    <PropertyBlock
                      key={property.id}
                      property={property}
                      onEditProperty={() => onEditProperty(property)}
                      onAddUnit={() => onAddUnit(property.id)}
                      onEditUnit={onEditUnit}
                    />
                  ))
                )}
              </Stack>
            </Box>

            <Divider />

            <UserSection
              title="Owners"
              emptyText="No owners."
              users={owners}
              onToggleUserStatus={onToggleUserStatus}
              onDeleteUser={onDeleteUser}
            />

            <UserSection
              title="Managers"
              emptyText="No managers."
              users={managers}
              onToggleUserStatus={onToggleUserStatus}
              onDeleteUser={onDeleteUser}
            />

            <UserSection
              title="Housekeepers"
              emptyText="No housekeepers."
              users={cleaners}
              onToggleUserStatus={onToggleUserStatus}
              onDeleteUser={onDeleteUser}
            />

            <UserSection
              title="Viewers"
              emptyText="No viewers."
              users={viewers}
              onToggleUserStatus={onToggleUserStatus}
              onDeleteUser={onDeleteUser}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton variant="outlined" onClick={onClose}>
          Close
        </AppButton>

        <AppButton variant="outlined" onClick={onOpenCleaner}>
          Add team member
        </AppButton>

        <AppButton onClick={onOpenSubscription}>Update subscription</AppButton>

        <AppButton color="error" onClick={onDeleteAccount}>
          Delete account now
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

interface PropertyBlockProps {
  property: AdminPropertyResponse;
  onEditProperty: () => void;
  onAddUnit: () => void;
  onEditUnit: (unit: AdminUnitResponse) => void;
}

const PropertyBlock = ({
  property,
  onEditProperty,
  onAddUnit,
  onEditUnit,
}: PropertyBlockProps) => {
  const units = property.units || [];

  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 900 }}>{property.name}</Typography>

          <Typography variant="body2" color="text.secondary">
            {[property.address, property.city].filter(Boolean).join(", ") ||
              "No address"}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <AppButton variant="outlined" onClick={onEditProperty}>
            Edit property
          </AppButton>

          <AppButton variant="outlined" onClick={onAddUnit}>
            Add unit
          </AppButton>
        </Stack>
      </Box>

      <Stack spacing={1} sx={{ mt: 1.5 }}>
        {units.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No units configured.
          </Typography>
        ) : (
          units.map((unit) => (
            <UnitRow
              key={unit.id}
              unit={unit}
              onEdit={() => onEditUnit(unit)}
            />
          ))
        )}
      </Stack>
    </Box>
  );
};

interface UnitRowProps {
  unit: AdminUnitResponse;
  onEdit: () => void;
}

const UnitRow = ({ unit, onEdit }: UnitRowProps) => {
  const airbnbConfigured = unit.external_calendars?.some(
    (calendar) => calendar.provider === "airbnb" && calendar.active,
  );

  const bookingConfigured = unit.external_calendars?.some(
    (calendar) => calendar.provider === "booking" && calendar.active,
  );

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: "action.hover",
        borderRadius: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 800 }}>{unit.name}</Typography>

        <Typography variant="body2" color="text.secondary">
          {readableText(unit.type)}
          {" · capacity "}
          {unit.capacity}
          {" · housekeeper: "}
          {unit.default_cleaner?.name || "none"}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          Airbnb iCal: {airbnbConfigured ? "configured" : "not configured"}
          {" · Booking.com iCal: "}
          {bookingConfigured ? "configured" : "not configured"}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
        }}
      >
        <Chip
          label={unit.active ? "Active" : "Inactive"}
          color={unit.active ? "success" : "default"}
          size="small"
        />

        <AppButton variant="outlined" onClick={onEdit}>
          Edit
        </AppButton>
      </Stack>
    </Box>
  );
};

interface UserSectionProps {
  title: string;
  emptyText: string;
  users: AdminUserResponse[];
  onToggleUserStatus: (user: AdminUserResponse) => void;
  onDeleteUser: (user: AdminUserResponse) => void;
}

const UserSection = ({
  title,
  emptyText,
  users,
  onToggleUserStatus,
  onDeleteUser,
}: UserSectionProps) => {
  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          mb: 1,
          fontWeight: 900,
        }}
      >
        {title}
      </Typography>

      <Stack spacing={1}>
        {users.length === 0 ? (
          <Typography color="text.secondary">{emptyText}</Typography>
        ) : (
          users.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              onToggle={() => onToggleUserStatus(user)}
              onDelete={() => onDeleteUser(user)}
            />
          ))
        )}
      </Stack>
    </Box>
  );
};

interface InfoBlockProps {
  label: string;
  value: string;
}

const InfoBlock = ({ label, value }: InfoBlockProps) => {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 900 }}>{value}</Typography>
    </Box>
  );
};

interface UserRowProps {
  user: AdminUserResponse;
  onToggle: () => void;
  onDelete: () => void;
}

const UserRow = ({ user, onToggle, onDelete }: UserRowProps) => {
  return (
    <Box
      sx={{
        p: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 900 }}>{user.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {user.email} · {getRoleLabel(user.role)}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
        }}
      >
        <Chip
          label={user.is_active === false ? "Inactive" : "Active"}
          color={user.is_active === false ? "error" : "success"}
          size="small"
          sx={{ fontWeight: 800 }}
        />

        <AppButton
          variant="outlined"
          color={user.is_active === false ? "success" : "error"}
          onClick={onToggle}
        >
          {user.is_active === false ? "Activate" : "Deactivate"}
        </AppButton>

        <AppButton variant="outlined" color="error" onClick={onDelete}>
          Delete
        </AppButton>
      </Stack>
    </Box>
  );
};

export default AccountDetailsModal;
